import i18n from 'i18n';
import _ from 'lodash';
import { getByteLength } from './StringUtil';
import {
  formatPriceWithCurrency,
  getCurrencyLabel
} from 'helper/CurrencyHelper';

export const URL_REGEX =
  /(https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/\S*)?(?:\?\S*)?(?:#\S*)?)/;

export function validateEmpty(value) {
  if (typeof value === 'number') {
    return;
  }
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && _.isEmpty(value)) ||
    (Array.isArray(value) && _.isEmpty(value))
  ) {
    return i18n.t('formValidate.labels.emptyError');
  }
}

export function validateStringMaxLength(
  value: string,
  maxLength: number,
  customErrorMessage?: string
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }
  const stringLength = getByteLength(value);
  if (stringLength > maxLength) {
    return customErrorMessage
      ? customErrorMessage
      : i18n.t('formValidate.labels.remainingCharacters', {
          num: maxLength - stringLength
        });
  }
}

export function validateValidWords(value: string, customErrorMessage?: string) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }
  // Check for emoji or special symbol using regex
  const emojiRE = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/u;
  const containsDisallowed = emojiRE.test(value);
  if (containsDisallowed) {
    return customErrorMessage
      ? customErrorMessage
      : i18n.t('formValidate.labels.validWordsError');
  }
}

export function validateMinimum(
  value: number | string | undefined,
  minValue: number,
  i18nKey = 'formValidate.labels.minimumError',
  currency = ''
) {
  const error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber =
    typeof value === 'string' ? parseFloat(value) : value || 0;
  if (valueNumber < minValue) {
    let min = getCurrencyLabel(currency) + minValue;
    return i18n.t(i18nKey, { min });
  }
}

export function validateMinMax(
  value: number | string | undefined,
  min: number,
  max: number
) {
  let error = validateMinimum(value, min);
  if (error) {
    return error;
  }

  const valueNumber =
    typeof value === 'string' ? parseFloat(value) : value || 0;
  if (valueNumber > max) {
    return i18n.t('formValidate.labels.maximumError', { max });
  }
}

export function validateMax(
  value: number | string | undefined,
  max: number,
  currency = '',
  i18nKey = 'formValidate.labels.maximumError'
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber =
    typeof value === 'string' ? parseFloat(value) : value || 0;
  if (valueNumber > max) {
    return i18n.t(i18nKey, {
      max: currency ? formatPriceWithCurrency(currency, max) : max
    });
  }
}

export function validateUrl(url: string | undefined): string | undefined {
  let error = validateEmpty(url);
  if (error) {
    return error;
  }

  const urlExpression =
    /(^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/;
  const regex = new RegExp(urlExpression);
  if (!url || !url.match(regex)) {
    return i18n.t('formValidate.labels.needUrl');
  }
}

export function validateEmail(email: string | undefined): string | undefined {
  if (_.isEmpty(email)) {
    return i18n.t('formValidate.labels.emailRequired');
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email!)) {
    return i18n.t('formValidate.labels.invalidEmail');
  }
  return undefined;
}

export function validateInteger(
  value: number | string | undefined
): string | undefined {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isInteger(valueNumber)) {
    return i18n.t('formValidate.labels.integerError');
  }
}

export function validateValueInOptions(
  options: SelectOptions[],
  value: string | number
) {
  const option = options.find((option) => option.value === value);
  if (!option) {
    return i18n.t('formValidate.labels.invalidValueError');
  }
}
